/**
 * src/pages/ProductDetail.jsx
 * Sizes shown here are real "Stock" rows — sold-out sizes render disabled
 * instead of disappearing, matching the real barcode-level availability.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { fetchProductBySlug } from "../lib/supabaseProducts";
import { useCart } from "../lib/cart";
import { PriceBlock } from "../components/PriceBlock";
import { slugify } from "../lib/slug";

// Edit this text directly — it's static, not pulled from the database.
// Description below it IS dynamic (product.description from Supabase).
const SHIPPING_TEXT = `We ship across India. Orders are dispatched within 1 business day (same-day within Gurgaon). Delivery typically takes 1 day in Gurgaon and 3-5 business days elsewhere in India.`;
const RETURN_TEXT = `Returns and exchanges are accepted within 3 days of delivery, provided the item is unused, unwashed, and has all original tags attached. To start a return, message us on WhatsApp with your order number.`;

function AccordionItem({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: "1px solid var(--border)" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "14px 0", background: "none", border: "none", cursor: "pointer",
          fontSize: 14, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", color: "var(--foreground)",
        }}
      >
        {title}
        <span style={{ fontSize: 18, fontWeight: 400 }}>{open ? "−" : "+"}</span>
      </button>
      {open && <div style={{ paddingBottom: 16, fontSize: 14, color: "var(--muted-foreground)", lineHeight: 1.6 }}>{children}</div>}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [added, setAdded] = useState(false);
  const autoAdvanceRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setAdded(false);
    fetchProductBySlug(slug).then((p) => {
      setProduct(p);
      setActiveImage(0);
      setSelectedSize(p?.sizes.find((s) => s.available) || null);
      setLoading(false);
    });
  }, [slug]);

  // Auto-advance the main image every 3.5s — restarts whenever the person
  // manually picks a thumbnail, and does nothing if there's only one image.
  useEffect(() => {
    if (!product || product.images.length <= 1) return;
    autoAdvanceRef.current = setInterval(() => {
      setActiveImage((i) => (i + 1) % product.images.length);
    }, 3500);
    return () => clearInterval(autoAdvanceRef.current);
  }, [product, activeImage]);

  function selectImage(i) {
    clearInterval(autoAdvanceRef.current);
    setActiveImage(i);
  }

  if (loading) return <div className="container section" style={{ paddingLeft: 16, paddingRight: 16, boxSizing: "border-box" }}><p className="muted">Loading…</p></div>;

  if (!product) {
    return (
      <div className="container section text-center" style={{ paddingLeft: 16, paddingRight: 16, boxSizing: "border-box" }}>
        <h1>Product not found</h1>
        <p className="muted mt-2">It may have been unlisted or sold out completely.</p>
        <Link to="/shop" className="btn btn-primary mt-6">Back to shop</Link>
      </div>
    );
  }

  const productUrl = `https://ritufashionstore.in/product/${product.slug}`;

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description,
    image: product.images,
    brand: product.brand || "Ritu Fashion Store",
    offers: {
      "@type": "Offer",
      url: productUrl,
      priceCurrency: "INR",
      price: product.priceFrom,
      availability: product.anyAvailable
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      areaServed: "Gurgaon",
    },
  };

  const breadcrumbItems = [
    { name: "Home", url: "https://ritufashionstore.in" },
    ...(product.gender ? [{ name: product.gender[0].toUpperCase() + product.gender.slice(1), url: `https://ritufashionstore.in/shop/${product.gender}` }] : []),
    ...(product.category ? [{ name: product.category, url: `https://ritufashionstore.in/shop/${product.gender}/${slugify(product.category)}` }] : []),
    { name: product.name, url: productUrl },
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbItems.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };

  const metaDescription =
    product.description?.slice(0, 130) ||
    `Shop ${product.subcategory || product.category || "kids clothing"} for ${product.gender || "kids"} in Gurgaon`;
  const fullMetaDescription = `${metaDescription} — Ritu Fashion Store, Gurgaon. 1-day delivery.`.slice(0, 155);

  function handleSizeSelect(s) {
    if (!s.available) return;
    setSelectedSize(s);
  }

  function handleAddToCart() {
    if (!selectedSize) return;
    addItem(product, selectedSize, 1);
    setAdded(true);
  }

  function handleBuyNow() {
    if (!selectedSize) return;
    addItem(product, selectedSize, 1);
    navigate("/cart", { state: { openCheckout: true } });
  }

  return (
    <div className="container section" style={{ paddingLeft: 16, paddingRight: 16, boxSizing: "border-box" }}>
      <Helmet>
        <title>{`${product.name} | ${product.subcategory || product.category || "Kids Wear"} in Gurgaon | Ritu Fashion Store`}</title>
        <meta name="description" content={fullMetaDescription} />
        <link rel="canonical" href={productUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.name} | Ritu Fashion Store`} />
        <meta property="og:description" content={fullMetaDescription} />
        {product.images[0] && <meta property="og:image" content={product.images[0]} />}
        <meta property="og:url" content={productUrl} />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Visible breadcrumb trail — helps both users and search engines understand the category path */}
      <nav aria-label="Breadcrumb" style={{ fontSize: 13, color: "var(--muted-foreground)", marginBottom: 16 }}>
        {breadcrumbItems.map((item, i) => (
          <span key={item.url}>
            {i > 0 && <span style={{ margin: "0 6px" }}>/</span>}
            {i === breadcrumbItems.length - 1 ? (
              <span style={{ color: "var(--foreground)", fontWeight: 600 }}>{item.name}</span>
            ) : (
              <Link to={item.url.replace("https://ritufashionstore.in", "")} style={{ color: "var(--muted-foreground)" }}>{item.name}</Link>
            )}
          </span>
        ))}
      </nav>

      <div className="product-detail-grid">
        {/* Gallery */}
        <div className="product-gallery">
          {product.images.length > 1 && (
            <div className="product-gallery-thumbs">
              {product.images.map((img, i) => (
                <button
                  key={img}
                  onClick={() => selectImage(i)}
                  style={{
                    borderRadius: 8, overflow: "hidden", padding: 0, cursor: "pointer",
                    border: i === activeImage ? "2px solid var(--primary)" : "1px solid var(--border)",
                    opacity: i === activeImage ? 1 : 0.7,
                  }}
                >
                  <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </button>
              ))}
            </div>
          )}
          <div className="product-gallery-main">
            <div className="product-card-img-wrap" style={{ aspectRatio: "9/11" }}>
              {product.images.length > 0 ? (
                <img src={product.images[activeImage]} alt={product.name} width={900} height={1100} style={{ transition: "opacity 0.3s ease" }} />
              ) : (
                <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", fontSize: 60 }}>👗</div>
              )}
            </div>
          </div>
        </div>

        {/* Details */}
        <div>
          <h1 style={{ fontSize: "1.75rem", fontWeight: 800 }}>{product.name}</h1>
          <p className="muted mt-2">{[product.category, product.subcategory].filter(Boolean).join(" · ")}</p>

          <div style={{ marginTop: 16 }}>
            <PriceBlock
              price={selectedSize?.effectivePrice ?? product.priceFrom}
              comparePrice={selectedSize?.basePrice ?? product.compareFrom}
            />
          </div>

          {/* Size selector — real Stock rows, sold-out shown disabled not hidden */}
          <div className="mt-6">
            <div className="filter-title">Age group</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
              {product.sizes.map((s) => {
                const isSelected = selectedSize?.stockId === s.stockId;
                return (
                  <button
                    key={s.stockId}
                    disabled={!s.available}
                    onClick={() => handleSizeSelect(s)}
                    style={{
                      padding: "10px 18px",
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: s.available ? "pointer" : "not-allowed",
                      border: isSelected ? "2px solid var(--foreground)" : "1px solid var(--border)",
                      background: isSelected ? "var(--foreground)" : s.available ? "var(--card)" : "var(--muted)",
                      color: isSelected ? "var(--card)" : s.available ? "var(--foreground)" : "var(--muted-foreground)",
                      textDecoration: s.available ? "none" : "line-through",
                      opacity: s.available ? 1 : 0.6,
                    }}
                  >
                    {s.sizeLabel || "One size"}
                  </button>
                );
              })}
            </div>
            {!product.anyAvailable && (
              <p style={{ color: "var(--destructive)", fontSize: 13, marginTop: 8, fontWeight: 600 }}>
                Sold out in all sizes right now.
              </p>
            )}
          </div>

          <button
            className="btn btn-outline mt-6"
            style={{ width: "100%", justifyContent: "center", border: "1px solid var(--foreground)" }}
            disabled={!selectedSize}
            onClick={handleAddToCart}
          >
            {added ? "✓ Added to bag" : "Add to Bag"}
          </button>
          <button
            className="btn mt-2"
            style={{ width: "100%", justifyContent: "center", background: "var(--foreground)", color: "var(--card)" }}
            disabled={!selectedSize}
            onClick={handleBuyNow}
          >
            Buy it now
          </button>
          {added && (
            <button className="btn btn-outline btn-sm mt-2" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/cart")}>
              View bag
            </button>
          )}

          <div className="mt-6">
            <AccordionItem title="Description" defaultOpen>
              {product.description || "No description added for this product yet."}
            </AccordionItem>
            <AccordionItem title="Shipping & Delivery">{SHIPPING_TEXT}</AccordionItem>
            <AccordionItem title="Return & Exchange">{RETURN_TEXT}</AccordionItem>
          </div>

          <p className="muted mt-6" style={{ fontSize: 13 }}>
            💬 Questions? <a href="https://wa.me/919873564717" target="_blank" rel="noopener noreferrer">WhatsApp us</a>
          </p>
        </div>
      </div>
    </div>
  );
}